import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft,
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Star,
  Clock,
  Calendar,
  DollarSign,
  FileText,
  AlertCircle,
  Loader2,
  Sparkles
} from 'lucide-react';
import hotelService, { Hotel } from '../../services/hotelService';
import RoomManagement from './RoomManagement';
import BlackoutDatesList from './BlackoutDatesList';
import HotelAmenities from './HotelAmenities';

const HotelView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'rooms' | 'blackout-dates' | 'amenities'>('details');

  useEffect(() => {
    if (id) {
      loadHotel();
    }
  }, [id]);

  const loadHotel = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await hotelService.getHotelById(parseInt(id!));
      setHotel(response.data);
    } catch (err) {
      setError('Failed to load hotel details. Please try again.');
      console.error('Error loading hotel:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-400'}`}
      />
    ));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-500 bg-green-100';
      case 'inactive':
        return 'text-red-500 bg-red-100';
      case 'suspended':
        return 'text-yellow-500 bg-yellow-100';
      default:
        return 'text-gray-500 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center h-64">
        <div className="flex items-center space-x-2 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading hotel details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2 p-4 bg-red-900/20 border border-red-800 rounded-md">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
        <Button onClick={() => navigate('/hotels')} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Hotels
        </Button>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="py-12">
        <Building2 className="w-12 h-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-300 mb-2">Hotel not found</h3>
        <p className="text-gray-400 mb-4">The hotel you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/hotels')} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Hotels
        </Button>
      </div>
    );
  }

  const acceptedPaymentMethods = hotel.accepted_payment_methods
    ? hotel.accepted_payment_methods
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button onClick={() => navigate('/hotels')} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {hotel.hotel_name}
              {hotel.extranet_hotel_id && (
                <span className="text-lg text-gray-400 ml-2">({hotel.extranet_hotel_id})</span>
              )}
            </h1>
            <p className="text-gray-400">Hotel Details</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button 
            onClick={() => navigate(`/hotels/${hotel.id}/edit`)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Edit Hotel
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-800">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('details')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Hotel Details
          </button>
          <button
            onClick={() => setActiveTab('amenities')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'amenities'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-2" />
            Amenities
          </button>
          <button
            onClick={() => setActiveTab('rooms')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'rooms'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Room Management
          </button>
          <button
            onClick={() => setActiveTab('blackout-dates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'blackout-dates'
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Blackout Dates
          </button>
          
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-400">Hotel ID</label>
                  <p className="text-white">{hotel.extranet_hotel_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Vervotech ID</label>
                  <p className="text-white">{hotel.vervotech_id || 'Not assigned'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Hotel Type</label>
                  <p className="text-white">{hotel.hotel_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Chain Brand</label>
                  <p className="text-white">{hotel.chain_brand || 'Independent'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(hotel.status)}`}>
                    {hotel.status}
                  </span>
                </div>
                {hotel.star_rating && (
                  <div>
                    <label className="text-sm font-medium text-gray-400">Star Rating</label>
                    <div className="flex items-center space-x-1">
                      {renderStars(hotel.star_rating)}
                      <span className="text-sm text-gray-400 ml-2">({hotel.star_rating} stars)</span>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-400">Destination</label>
                  <p className="text-white">{hotel.destination}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <MapPin className="w-5 h-5" />
                Address
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-400">Street Address</label>
                  <p className="text-white">{hotel.street_address}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Area</label>
                  <p className="text-white">{hotel.area || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Landmark</label>
                  <p className="text-white">{hotel.landmark || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">City</label>
                  <p className="text-white">{hotel.city}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">State</label>
                  <p className="text-white">{hotel.state}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Country</label>
                  <p className="text-white">{hotel.country}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">ZIP Code</label>
                  <p className="text-white">{hotel.zip_code || 'N/A'}</p>
                </div>
              </div>
              {hotel.latitude && hotel.longitude && (
                <div>
                  <label className="text-sm font-medium text-gray-400">Coordinates</label>
                  <p className="text-white">{hotel.latitude}, {hotel.longitude}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {hotel.phone && (
                  <div>
                    <label className="text-sm font-medium text-gray-400 flex items-center space-x-2">
                      <Phone className="w-4 h-4" />
                      Phone
                    </label>
                    <p className="text-white">{hotel.phone}</p>
                  </div>
                )}
                {hotel.reservation_email && (
                  <div>
                    <label className="text-sm font-medium text-gray-400 flex items-center space-x-2">
                      <Mail className="w-4 h-4" />
                      Reservation Email
                    </label>
                    <p className="text-white">{hotel.reservation_email}</p>
                  </div>
                )}
                {hotel.reservation_contact_number && (
                  <div>
                    <label className="text-sm font-medium text-gray-400 flex items-center space-x-2">
                      <Phone className="w-4 h-4" />
                      Reservation Contact
                    </label>
                    <p className="text-white">{hotel.reservation_contact_number}</p>
                  </div>
                )}
                {hotel.support_email && (
                  <div>
                    <label className="text-sm font-medium text-gray-400 flex items-center space-x-2">
                      <Mail className="w-4 h-4" />
                      Support Email
                    </label>
                    <p className="text-white">{hotel.support_email}</p>
                  </div>
                )}
                {hotel.hotel_website && (
                  <div>
                    <label className="text-sm font-medium text-gray-400 flex items-center space-x-2">
                      <Globe className="w-4 h-4" />
                      Website
                    </label>
                    <a 
                      href={hotel.hotel_website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      {hotel.hotel_website}
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Owner / Management */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Owner / Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-400">Contact Person</label>
                  <p className="text-white">{hotel.contact_person_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Designation</label>
                  <p className="text-white">{hotel.designation || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Contact Email</label>
                  <p className="text-white">{hotel.contact_email || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Contact Phone</label>
                  <p className="text-white">{hotel.contact_phone || 'N/A'}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-400">Tax Details</label>
                <p className="text-sm text-gray-300 whitespace-pre-line">
                  {hotel.tax_details || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-400">Bank Account Details</label>
                <p className="text-sm text-gray-300 whitespace-pre-line">
                  {hotel.bank_account_details || 'N/A'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Operational Details */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <Clock className="w-5 h-5" />
                Operational Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-400">Check-in Time</label>
                  <p className="text-white">{hotel.checkin_time || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Check-out Time</label>
                  <p className="text-white">{hotel.checkout_time || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Minimum Stay</label>
                  <p className="text-white">{hotel.min_stay || 'N/A'} nights</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Maximum Stay</label>
                  <p className="text-white">{hotel.max_stay || 'N/A'} nights</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Max Advance Booking</label>
                  <p className="text-white">{hotel.max_advance_booking_days || 'N/A'} days</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400">Timezone</label>
                  <p className="text-white">{hotel.timezone || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          {(hotel.hotel_description || hotel.terms_conditions) && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Description & Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {hotel.hotel_description && (
                  <div>
                    <label className="text-sm font-medium text-gray-400">Hotel Description</label>
                    <p className="text-gray-300">{hotel.hotel_description}</p>
                  </div>
                )}
                {hotel.terms_conditions && (
                  <div>
                    <label className="text-sm font-medium text-gray-400">Terms & Conditions</label>
                    <p className="text-gray-300">{hotel.terms_conditions}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Pricing */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <DollarSign className="w-5 h-5" />
                Pricing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-400">Currency</label>
                <p className="text-white">{hotel.currency || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-400">Markup Percentage</label>
                <p className="text-white">{hotel.markup_percentage || 'N/A'}%</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-400">Service Charge</label>
                <p className="text-white">{hotel.service_charge_percentage || 'N/A'}%</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-400">Child Rate</label>
                <p className="text-white">₹{hotel.child_rate || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-400">Extra Adult Rate</label>
                <p className="text-white">₹{hotel.extra_adult_rate || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-400">Extra Bed Rate</label>
                <p className="text-white">₹{hotel.extra_bed_rate || 'N/A'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Policies */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <FileText className="w-5 h-5" />
                Policies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="border border-gray-700 rounded-md p-4">
                <h4 className="text-gray-300 font-medium mb-3">Accepted Payment Methods</h4>
                {acceptedPaymentMethods.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {acceptedPaymentMethods.map(method => (
                      <span
                        key={method}
                        className="px-3 py-1 text-sm rounded-full bg-blue-900/30 border border-blue-700 text-blue-300"
                      >
                        {method}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No payment methods configured.</p>
                )}
              </div>

              {/* Cancellation Policy */}
              {(hotel.cancellation_policy_text || hotel.cancellation_days || hotel.cancellation_percentage) && (
                <div className="border border-gray-700 rounded-md p-4">
                  <h4 className="text-gray-300 font-medium mb-3">Cancellation Policy</h4>
                  {hotel.cancellation_policy_text && (
                    <div className="mb-3">
                      <label className="text-sm font-medium text-gray-400">Policy Text</label>
                      <p className="text-sm text-gray-300">{hotel.cancellation_policy_text}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    {hotel.cancellation_days && (
                      <div>
                        <label className="text-sm font-medium text-gray-400">Days before check-in</label>
                        <p className="text-white">{hotel.cancellation_days} days</p>
                      </div>
                    )}
                    {hotel.cancellation_percentage && (
                      <div>
                        <label className="text-sm font-medium text-gray-400">Percentage deduction</label>
                        <p className="text-white">{hotel.cancellation_percentage}%</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* No Show Policy */}
              {(hotel.no_show_policy_text || hotel.no_show_percentage) && (
                <div className="border border-gray-700 rounded-md p-4">
                  <h4 className="text-gray-300 font-medium mb-3">No Show Policy</h4>
                  {hotel.no_show_policy_text && (
                    <div className="mb-3">
                      <label className="text-sm font-medium text-gray-400">Policy Text</label>
                      <p className="text-sm text-gray-300">{hotel.no_show_policy_text}</p>
                    </div>
                  )}
                  {hotel.no_show_percentage && (
                    <div>
                      <label className="text-sm font-medium text-gray-400">Percentage deduction</label>
                      <p className="text-white">{hotel.no_show_percentage}%</p>
                    </div>
                  )}
                </div>
              )}

              {/* Child Policy */}
              {hotel.child_policy && (
                <div>
                  <label className="text-sm font-medium text-gray-400">Child Policy</label>
                  <p className="text-sm text-gray-300">{hotel.child_policy}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Special Offers */}
          {hotel.special_offer_name && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center space-x-2">
                  <Calendar className="w-5 h-5" />
                  Special Offers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-400">Offer Name</label>
                  <p className="text-white">{hotel.special_offer_name}</p>
                </div>
                {hotel.special_offer_type && (
                  <div>
                    <label className="text-sm font-medium text-gray-400">Offer Type</label>
                    <p className="text-white">{hotel.special_offer_type}</p>
                  </div>
                )}
                {hotel.special_offer_details && (
                  <div>
                    <label className="text-sm font-medium text-gray-400">Details</label>
                    <p className="text-sm text-gray-300">{hotel.special_offer_details}</p>
                  </div>
                )}
                {hotel.offer_valid_from && (
                  <div>
                    <label className="text-sm font-medium text-gray-400">Valid From</label>
                    <p className="text-white">{new Date(hotel.offer_valid_from).toLocaleDateString()}</p>
                  </div>
                )}
                {hotel.offer_valid_to && (
                  <div>
                    <label className="text-sm font-medium text-gray-400">Valid To</label>
                    <p className="text-white">{new Date(hotel.offer_valid_to).toLocaleDateString()}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Additional Information */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-400">Applicable Market</label>
                <p className="text-white">{hotel.applicable_market || 'N/A'}</p>
              </div>
              {hotel.remarks && (
                <div>
                  <label className="text-sm font-medium text-gray-400">Remarks</label>
                  <p className="text-sm text-gray-300">{hotel.remarks}</p>
                </div>
              )}
              {hotel.special_conditions && (
                <div>
                  <label className="text-sm font-medium text-gray-400">Special Conditions</label>
                  <p className="text-sm text-gray-300">{hotel.special_conditions}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-400">Created</label>
                <p className="text-white">{new Date(hotel.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-400">Last Updated</label>
                <p className="text-white">{new Date(hotel.updated_at).toLocaleDateString()}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      )}

      {activeTab === 'rooms' && (
        <RoomManagement hotelId={hotel.id} />
      )}

      {activeTab === 'blackout-dates' && (
        <BlackoutDatesList hotelId={hotel.id} />
      )}

      {activeTab === 'amenities' && (
        <HotelAmenities hotelId={hotel.id} />
      )}
    </div>
  );
};

export default HotelView;
